import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { runInAction, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import HelpMeDecideContent from 'components/common/helpMeDecide/HelpMeDecideContent';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { useQueryParams } from 'global/hooks/useQuery';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import DynamicFormModal from 'components/common/modals/DynamicFormModal';
import { createStyles } from 'components/common/helpMeDecide/commonCriteriaStyles';
import { ExtendedFilter } from '@insurancemenu/imenu-components-lib/dist/types/filterSidebarTypings';
import { INITIAL_FILTER } from 'store/ui-stores/browsing/browsingFilterStore';
import DentalCriteria1 from '../GroupDental/Criteria1';
import DentalCriteria2 from '../GroupDental/Criteria2';
import DentalCriteria3 from '../GroupDental/Criteria3';
import DentalCriteria4 from '../GroupDental/Criteria4';
import VisionCriteria1 from '../GroupVision/Criteria1';
import VisionCriteria2 from '../GroupVision/Criteria2';
import VisionCriteria3 from '../GroupVision/Criteria3';

export interface HelpMeDecideDDIAProps {
  numberOfDentalCriteria: number;
  numberOfVisionCriteria: number;
}

const HelpMeDecideDDIA = observer(
  ({ numberOfVisionCriteria, numberOfDentalCriteria }: HelpMeDecideDDIAProps): JSX.Element => {
    const classes = createStyles();
    const history = useHistory();
    //  Query params
    const query = useQueryParams();
    const cartId = query.get('cartId') || null;
    const pLineId = query.get('productLineId') || null;
    const groupId = query.get('groupId') || null;

    // Mobx store
    const {
      helpMeDecideStore: { criteriaIndex, setCriteriaIndex, setCurrentProductLine },
      browsingStore: {
        loading,
        currentProductLineId,
        cart,
        productLines,
        allPortfolios,
        productLineAttrs,
        setFilteredPortfolios,
        setCurrentProductLineId,
        collectInitialData,
        getPortfolios,
        getCatalog,
        updateCart,
        setLoading,
      },
      browsingCriteriaModalStore: {
        openBrowsingCriteriaModal,
        closeBrowsingCriteriaModal,
        saveBrowsingCriteria,
        showBrowsingCriteriaModal,
        dynamicForm,
        form,
        loading: bcModalLoading,
      },
      browsingFilterStore: {
        filters,
        filterPortfoliosBy,
        setFilters,
        removeFilterFromNextCriteria,
        onBooleanCheckboxFilterChange,
      },
      domainStore: { domainHeader },
    } = useRootStore();

    useEffect(() => {
      if (cartId && pLineId && groupId) {
        void collectInitialData(groupId, cartId, pLineId as ProductLineId);
        setCurrentProductLine(pLineId as ProductLineId);
      }
    }, []);

    const convertedCart = toJS(cart);
    const convertedDomain = toJS(domainHeader);
    const convertedFilters = toJS(filters);
    const convertedProductLines = productLines.map((line) => toJS(line)).slice();
    const convertedAllPortfolios = allPortfolios.map((portfolio) => toJS(portfolio)).slice();
    const convertedProductLineAttrs = productLineAttrs.map((attr) => toJS(attr)).slice();

    const effectiveDate = convertedCart.lastUpdateBrowsingCriteria?.effectiveDate;
    const rateSegment = currentProductLineId ? PRODUCT_LINES_CONTENT_MAP[currentProductLineId].rateSegment : 'GROUP';

    const productLineIdFromUrl = (): ProductLineId => {
      return pLineId ? (pLineId as ProductLineId) : 'GroupDental';
    };

    const getNumberOfCriteria = (): number => {
      switch (pLineId) {
        case 'GroupDental':
          return numberOfDentalCriteria;
        case 'GroupVision':
          return numberOfVisionCriteria;
        default:
          return 0;
      }
    };

    const openBcModal = (): void => {
      const selectedProductLineId = productLineIdFromUrl();

      setCurrentProductLineId(selectedProductLineId);
      const pLine = convertedProductLines.find((line) => line.productLineId === selectedProductLineId);
      void openBrowsingCriteriaModal(
        convertedCart,
        selectedProductLineId,
        pLine?.eStoreBcFormId || pLine?.bcFormId || '',
      );
    };

    const saveModalChanges = async (currentFilters: ExtendedFilter | null): Promise<void> => {
      if (!currentProductLineId) throw new Error('Modal cannot be opened without currentProductLineId in the store');
      setLoading(true);
      const updatedCart = await saveBrowsingCriteria();
      await runInAction(async () => {
        updateCart(updatedCart);

        const requests = [
          getCatalog(effectiveDate, rateSegment, currentProductLineId, updatedCart.lastUpdateBrowsingCriteria.state),
          getPortfolios({
            bc: updatedCart.lastUpdateBrowsingCriteria,
            rateSegment,
            sponsorId: convertedDomain.id,
            agencyId: '',
            productLineId: currentProductLineId,
            groupId: updatedCart.groupId,
          }),
        ];
        await Promise.all(requests).then(() => {
          if (currentFilters) {
            const updatedPortfolios = filterPortfoliosBy(
              [...convertedAllPortfolios],
              currentFilters,
              convertedProductLineAttrs,
            );
            setFilteredPortfolios(updatedPortfolios);
          }
          closeBrowsingCriteriaModal();
          setLoading(false);
          history.push(
            `/browsing?groupId=${groupId}&cartId=${cartId}&productLineId=${currentProductLineId}&cartType=browsing&backType=help-me-decide`,
          );
        });
      });
    };

    const changeCriteria = (type: 'BACK' | 'NEXT'): void => {
      const isFirstCriteria = criteriaIndex === 1;
      const isLastCriteria = criteriaIndex === getNumberOfCriteria();

      switch (type) {
        case 'BACK':
          if (!isFirstCriteria) {
            setCriteriaIndex(criteriaIndex - 1);
          } else {
            setFilters(INITIAL_FILTER);
            const url = `/popular-plans?groupId=${groupId}&productLineId=${pLineId}&cartId=${cartId}&cartType=popular`;
            history.push(url);
          }
          if (pLineId) removeFilterFromNextCriteria(convertedDomain.id, criteriaIndex, pLineId as ProductLineId);
          break;
        case 'NEXT':
          if (isLastCriteria) {
            openBcModal();
          } else {
            setCriteriaIndex(criteriaIndex + 1);
          }
          break;
        default:
          history.goBack();
          break;
      }
    };

    const getCurrentCriteria = (productLineId: ProductLineId | null): JSX.Element => {
      // These props are used to render criteria form
      const commonCriteriaProps = {
        numberOfCriteria: getNumberOfCriteria(),
        minHeight: '58rem',
        onContinue: (): void => {
          changeCriteria('NEXT');
        },
        onBack: (): void => {
          changeCriteria('BACK');
        },
      };

      switch (productLineId) {
        case 'GroupDental':
          switch (criteriaIndex) {
            case 1:
              return <DentalCriteria1 {...commonCriteriaProps} />;
            case 2:
              return <DentalCriteria2 {...commonCriteriaProps} />;
            case 3:
              return <DentalCriteria3 {...commonCriteriaProps} />;
            case 4:
              return <DentalCriteria4 {...commonCriteriaProps} />;
            default:
              return <></>;
          }
        case 'GroupVision':
          switch (criteriaIndex) {
            case 1:
              return <VisionCriteria1 {...commonCriteriaProps} />;
            case 2:
              return <VisionCriteria2 {...commonCriteriaProps} />;
            case 3:
              return <VisionCriteria3 {...commonCriteriaProps} />;
            default:
              return <></>;
          }
        default:
          return <></>;
      }
    };

    return (
      <>
        <HelpMeDecideContent formContent={getCurrentCriteria(pLineId ? (pLineId as ProductLineId) : null)} />
        <DynamicFormModal
          show={showBrowsingCriteriaModal}
          onClose={(): void => {
            // If BC modal wasn't submitted - set default ACA value - all
            onBooleanCheckboxFilterChange('ACA', 'all');
            closeBrowsingCriteriaModal();
          }}
          onAction={(): void => {
            void saveModalChanges(convertedFilters);
          }}
          actionButtonText="View Plans"
          dynamicForm={dynamicForm}
          form={form}
          loading={bcModalLoading || loading}
          className={classes.bcModal}
        />
      </>
    );
  },
);

export default HelpMeDecideDDIA;
